/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    EXPORT_POLLING_HOOK
 * Date:         2026-02-08
 * Modified By:  Claude
 *
 * Description:
 * App-level hook for polling export jobs. Persists across route navigation
 * by storing pending jobId in localStorage and resuming on mount.
 *
 * File Size Check:
 * - Current: ~80 lines
 * - Projected: ~80 lines
 * - Decision: New file
 *
 * Related Files:
 * - client/src/contexts/ExportContext.tsx
 * - client/src/features/inventory/InventoryPage.tsx
 */

import { useRef, useState, useCallback, useEffect } from "react";
import { notify } from "../utils/notifications";
import { getExportStatus, downloadExport } from "../services/inventoryService";

const STORAGE_KEY = "pendingExportJobId";
const POLL_INTERVAL = 3000; // 3 seconds
const MAX_ATTEMPTS = 60; // 60 × 3s = 3 minutes max

export function useExportPolling() {
  const exportPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const pollExportJob = useCallback((jobId: string) => {
    setIsExporting(true);

    // Clear any existing poll
    if (exportPollRef.current) clearInterval(exportPollRef.current);

    let attempts = 0;

    exportPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await getExportStatus(jobId);
        const { status, error } = res.data;

        if (status === "DONE") {
          clearInterval(exportPollRef.current!);
          exportPollRef.current = null;
          localStorage.removeItem(STORAGE_KEY);

          // Trigger browser download
          const blob = await downloadExport(jobId);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `inventory_export_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          notify.success("Inventory exported successfully");
          setIsExporting(false);
        } else if (status === "FAILED") {
          clearInterval(exportPollRef.current!);
          exportPollRef.current = null;
          localStorage.removeItem(STORAGE_KEY);
          notify.error(null, error || "Export failed. Please try again.");
          setIsExporting(false);
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(exportPollRef.current!);
          exportPollRef.current = null;
          notify.warning("Export is taking longer than expected. We'll keep processing — check back shortly.");
          setIsExporting(false);
        }
      } catch {
        // Network error — keep polling, don't give up
      }
    }, POLL_INTERVAL);
  }, []);

  const startExport = useCallback(
    (jobId: string) => {
      localStorage.setItem(STORAGE_KEY, jobId);
      pollExportJob(jobId);
    },
    [pollExportJob]
  );

  // Resume on app mount if there's a pending job
  useEffect(() => {
    const pendingJobId = localStorage.getItem(STORAGE_KEY);
    if (pendingJobId) {
      notify.info("Resuming your export...");
      pollExportJob(pendingJobId);
    }

    // Cleanup on unmount
    return () => {
      if (exportPollRef.current) {
        clearInterval(exportPollRef.current);
        exportPollRef.current = null;
      }
    };
  }, [pollExportJob]);

  return { isExporting, startExport };
}
